import type { GameSession } from '../game/session';
import { must, replayAnimation } from './dom';

/** Everything the win panel shows, resolved from game state — no DOM involved. */
export interface ResultView {
  title: string;
  /** The reference's topology name, stamped under the title on a perfect. */
  topology?: string;
  /** Chips, in order. Each may contain `<b>` emphasis. */
  stats: string[];
  /** Nudge under the chips; empty when there's nothing useful to say. */
  note: string;
  /** The capstone note gets the stamped-seal treatment (STYLEGUIDE §8). */
  engineers?: boolean;
  /** What the MW ratio is a ratio of, in cells (GDD §3.4). */
  raw?: string;
  /** One line per load-bearing number, until the player's first perfect. */
  legend?: string[];
}

export interface ResultInputs {
  /** MW of the layout currently on the board, or `null` if it's gone dark. */
  nowMW: number | null;
  /** Day streak to advertise, or `null` to leave it out. */
  streak: number | null;
  /**
   * A move count an older version of the game recorded for this grid, or
   * `null` where there is none. The counter is retired (GDD §9); this is a
   * reading kept, never one taken.
   */
  retiredMoves?: number | null;
  /** Name of the reference's topology, or `null` to leave it unstamped. */
  topology?: string | null;
  /**
   * Explain the numbers. On until the player's first perfect grid: cold reads
   * average 69 MW across a 35 to 100 spread, and a new player has no frame
   * for why a number keeps rising (GDD §6.2).
   */
  explain?: boolean;
}

/** Board units per cell, for reading raw wire in the unit the board is drawn in. */
const CELL = 100;

/**
 * What the three load-bearing numbers mean, in the operator's voice: a fact
 * each, no exclamation, no second sentence that isn't needed.
 */
const LEGEND = [
  'MW is delivery efficiency. Shorter wire, more power.',
  "Target is the engineers' own grid. Match it.",
  'Rank climbs from apprentice to superconductor.',
];

/**
 * The panel is deliberately not a "you won, stop playing" screen — it keeps
 * showing the current layout against the player's best so tuning stays live.
 */
export function buildResultView(session: GameSession, inputs: ResultInputs): ResultView {
  const best = session.best;
  const tier = session.tier();
  if (!best || !tier) throw new Error('buildResultView() before the grid was ever completed');

  const { nowMW, streak, retiredMoves, topology, explain } = inputs;
  const matched = session.matchedReference;

  // Chips follow the operator readout pattern: dim uppercase label (<i>),
  // mono value (<b>). The tier keeps its emoji for the share; here it is a
  // rank in plain text.
  //
  // The now/best chip keeps one shape whatever the board is doing — `now`
  // shows a dash when the grid is dark, like the HUD. A slot that appears
  // only when the numbers diverge rewraps the whole card on every tap.
  const stats: string[] = [
    `<i>now</i> <b class="mw">${nowMW ?? '–'}</b> · <i>best</i> <b class="mw">${best.mw}</b> <i>MW</i>`,
  ];
  if (session.firstMW != null && session.firstMW !== best.mw) {
    stats.push(`<i>first take</i> <b>${session.firstMW}</b>`);
  }
  stats.push(
    `<i>target</i> <b>${session.puzzle.refMW}</b>`,
    `<i>rank</i> <b>${tier.short}</b>`,
    `<i>towers</i> <b${session.underPar ? ' class="under-par"' : ''}>${best.towers.length}/${session.puzzle.par}</b>`,
  );
  if (streak != null && streak > 1) stats.push(`<i>streak</i> <b>${streak}</b>`);
  // Dimmed and unadorned: a fact from a version of the game that counted
  // moves, not an award this one still hands out (STYLEGUIDE §7).
  if (retiredMoves != null) {
    stats.push(`<span class="retired"><i>moves</i> <b>${retiredMoves}</b></span>`);
  }

  const engineers = matched && session.beatReference;
  // One note, and the biggest one wins: beating the engineers outranks the
  // flawless callout, which outranks having nothing left to say.
  const note = matched
    ? session.beatReference
      ? 'You beat the engineers.'
      : session.flawless
        ? 'Flawless. Never took a tower down.'
        : ''
    : nowMW == null
      ? 'Grid dark. Rebuild to keep tuning.'
      : (wireGapNote(session) ?? 'Keep tuning. Your best counts.');

  return {
    title: matched ? 'PERFECT GRID!' : 'GRID ENERGIZED',
    ...(matched && topology ? { topology } : {}),
    stats,
    note,
    engineers,
    ...(rawWire(session) ? { raw: rawWire(session)! } : {}),
    ...(explain ? { legend: LEGEND } : {}),
  };
}

/**
 * The raw wire on both sides, in cells.
 *
 * On every solved card, not only near the target. "84 MW" is a ratio, and a
 * ratio with nothing beside it is a grade rather than a measurement — this is
 * the line that says what it is a ratio *of*, from the first solve onward. It
 * is also the honest form of a thing that was briefly drawn on the board and
 * cut: the score's referent belongs in a readout, where it is a number, not on
 * the map, where it looked like a route (GDD §9).
 */
function rawWire(session: GameSession): string | null {
  const best = session.best;
  if (!best) return null;
  const ref = session.puzzle.ref.wire;
  return `your wire ${(best.wire / CELL).toFixed(2)} · reference ${(ref / CELL).toFixed(2)}`;
}

/**
 * Inside the Master band the rounded MW goes numb — two different layouts can
 * read the same — so the nudge switches to the real distance in wire. It is a
 * distance, not a direction, so it stays spoiler-free.
 */
function wireGapNote(session: GameSession): string | null {
  const best = session.best;
  if (!best) return null;
  const ref = session.puzzle.ref.wire;
  if (ref / best.wire < 0.96) return null;
  const pct = ((best.wire - ref) / best.wire) * 100;
  return `Reference wire is ${pct.toFixed(1)}% shorter.`;
}

/** Gap between chips as the card counts itself out, in ms. */
export const BEAT_GAP = 110;

export interface ShowOptions {
  /**
   * Reveal the chips one at a time instead of all at once. Reserved for
   * moments worth marking — the first solve, and the perfect grid — so that
   * routine re-tuning doesn't replay the fanfare on every tap.
   */
  stage?: boolean;
  /** Called once per chip as it lands, for the accompanying beat. */
  onBeat?: (index: number, total: number) => void;
}

/** Renders {@link ResultView}, flashing only when something actually changed. */
export class ResultPanel {
  private lastHTML = '';
  private beats: ReturnType<typeof setTimeout>[] = [];

  show(view: ResultView, opts: ShowOptions = {}): void {
    const panel = must('winPanel');
    panel.classList.add('show');
    // Publish the cadence to the stylesheet, so the CSS reveal and the JS
    // beats share one clock instead of two hardcoded 110s.
    panel.style.setProperty('--beat-gap', `${BEAT_GAP}ms`);
    must('winTitle').textContent = view.title;

    // The topology name is a stamp on the title, not a chip: it names the
    // shape the reference turned out to be, which is a fact about the day
    // rather than a reading off the player's board (GDD §3.5).
    const topo = must('winTopology');
    topo.textContent = view.topology ?? '';
    topo.hidden = !view.topology;

    const chips = view.stats
      .map((s, i) => `<span class="stat" style="--beat:${i}">${s}</span>`)
      .join('');
    // Everything below the chips keeps counting on from where they stopped, so
    // the staged reveal stays one cadence rather than restarting per block.
    let beat = view.stats.length;
    const html =
      `<span class="win-stats">${chips}</span>` +
      (view.note
        ? `<span class="win-note${view.engineers ? ' engineers' : ''}" style="--beat:${beat++}">${view.note}</span>`
        : '') +
      (view.raw ? `<span class="win-raw" style="--beat:${beat++}">${view.raw}</span>` : '') +
      (view.legend ?? [])
        .map((line) => `<span class="win-legend" style="--beat:${beat++}">${line}</span>`)
        .join('');

    if (html + (view.topology ?? '') === this.lastHTML && !opts.stage) return;

    const text = must('winText');
    this.clearBeats();
    text.innerHTML = html;
    text.classList.toggle('staged', Boolean(opts.stage));

    if (opts.stage) {
      replayAnimation(must('winTitle'), 'stamp');
      replayAnimation(must('winPanel'), 'celebrate');
      this.scheduleBeats(view, opts.onBeat);
    } else {
      replayAnimation(text, 'tick');
    }

    this.lastHTML = html + (view.topology ?? '');
  }

  /** Fire one beat per chip, in step with the CSS reveal. */
  private scheduleBeats(view: ResultView, onBeat?: ShowOptions['onBeat']): void {
    if (!onBeat) return;
    const total = view.stats.length;
    for (let i = 0; i < total; i++) {
      this.beats.push(setTimeout(() => onBeat(i, total), i * BEAT_GAP));
    }
  }

  private clearBeats(): void {
    for (const t of this.beats) clearTimeout(t);
    this.beats = [];
  }

  hide(): void {
    this.clearBeats();
    must('winPanel').classList.remove('show');
    this.lastHTML = '';
  }

  /** Bring the panel into view after the surge lands. */
  scrollIntoView(smooth: boolean): void {
    must('winPanel').scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
  }
}
